/**
 * The live EmbeddingsPort: JSON over fetch to the pandora-embeddings service,
 * straight from the browser (docs/agent.md §9).
 *
 * Kept apart from semantic.ts so the tools stay transport-free — tests and evals
 * hand in a recording fake instead, and nothing in the tool code changes.
 *
 * `observe` is the same hook `tools/port.ts` threads into the pan.do/ra client:
 * every request is reported as a `PandoraWireCall` so the step inspector shows
 * it next to the archive requests. `action` is "POST /api/search/shots" rather
 * than a pandora action name, which is what the inspector's curl builder keys
 * on to emit a JSON request instead of a form-encoded one.
 */
import type { SemanticConfig } from '../../api/archives';
import type { PandoraObserver, PandoraWireCall } from '../../api/pandora';
import type { EmbeddingsPort } from './embeddingsTypes';

/**
 * A hybrid shot search costs ~3 s on the service's CPU and a long trajectory
 * a few more; the loop's own wall clock is 180 s. Well inside that, well past
 * anything that is still going to answer.
 */
export const EMBEDDINGS_TIMEOUT_MS = 60_000;

export function createEmbeddingsPort(
  cfg: SemanticConfig,
  observe?: PandoraObserver,
): EmbeddingsPort {
  const base = cfg.url.replace(/\/+$/, '');

  async function call<T>(method: 'GET' | 'POST', path: string, data?: unknown): Promise<T> {
    const started = Date.now();
    // An observer is a debugging aid and must never be able to break a request.
    const emit = (extra: Omit<PandoraWireCall, 'site' | 'action' | 'data' | 'ms'>) => {
      if (!observe) return;
      try {
        observe({ site: base, action: `${method} ${path}`, data: data ?? {}, ms: Date.now() - started, ...extra });
      } catch {
        /* ignore */
      }
    };

    let url = `${base}${path}`;
    const init: RequestInit = { method };
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      init.signal = AbortSignal.timeout(EMBEDDINGS_TIMEOUT_MS);
    }
    if (method === 'GET') {
      if (data) {
        const qs = new URLSearchParams(data as Record<string, string>).toString();
        if (qs) url += `?${qs}`;
      }
    } else {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify(data ?? {});
    }

    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      emit({ ok: false, error });
      throw new Error(`embeddings ${path}: ${error}`);
    }

    // Read the body BEFORE the status check, so a failing request still records
    // what the server actually said.
    const raw = await res.text();
    const bytes = new TextEncoder().encode(raw).length;

    if (!res.ok) {
      // django-ninja errors are `{"detail": ...}`; say that rather than the status alone.
      let detail = raw.slice(0, 300);
      try {
        const parsed = JSON.parse(raw) as { detail?: unknown };
        if (parsed && typeof parsed.detail === 'string') detail = parsed.detail;
        else if (parsed && parsed.detail !== undefined) detail = JSON.stringify(parsed.detail);
      } catch {
        /* keep the raw text */
      }
      emit({ ok: false, httpStatus: res.status, bytes, responseText: raw, error: detail });
      throw new Error(`embeddings ${path}: HTTP ${res.status} — ${detail}`);
    }

    let parsed: T;
    try {
      parsed = JSON.parse(raw) as T;
    } catch {
      emit({ ok: false, httpStatus: res.status, bytes, responseText: raw, error: 'response was not JSON' });
      throw new Error(`embeddings ${path}: response was not JSON`);
    }
    emit({ ok: true, httpStatus: res.status, bytes, responseText: raw });
    return parsed;
  }

  return {
    archive: cfg.archive,
    searchShots: (body) => call('POST', '/api/search/shots', body),
    searchScenes: (body) => call('POST', '/api/search/scenes', body),
    searchRegions: (body) => call('POST', '/api/search/regions', body),
    similarShots: (body) => call('POST', '/api/similar/shots', body),
    steer: (body) => call('POST', '/api/steer', body),
    trajectory: (body) => call('POST', '/api/trajectory', body),
    axisScore: (body) => call('POST', '/api/axis/score', body),
  };
}
